import { useState, useCallback } from 'react';
import { reportService } from '../services';
import { useAuth } from '../context/AuthContext';
import { DauReportsResponse } from '../types/reports';

export interface DauReportsParams {
  startDate?: string;
  endDate?: string;
  provider?: string;
  tenant?: string;
}

export interface UseDauReportsState {
  data: DauReportsResponse | null;
  loading: boolean;
  error: string | null;
}

export interface UseDauReportsResult extends UseDauReportsState {
  fetchReports: (params?: DauReportsParams) => Promise<void>;
  refetch: () => Promise<void>;
  clearError: () => void;
}

export const useDauReports = (initialParams?: DauReportsParams): UseDauReportsResult => {
  const { isAuthenticated, user } = useAuth();
  const [state, setState] = useState<UseDauReportsState>({
    data: null,
    loading: false,
    error: null,
  });
  
  const [currentParams, setCurrentParams] = useState<DauReportsParams>(initialParams || {});

  const fetchReports = useCallback(async (params: DauReportsParams = {}) => {
    if (!isAuthenticated || !user?.roles?.includes('reportsReader')) {
      const errorMessage = 'Access denied: reportsReader role required';
      setState(prev => ({ 
        ...prev, 
        error: errorMessage,
        loading: false 
      }));
      throw new Error(errorMessage);
    }

    setState(prev => ({ ...prev, loading: true, error: null }));
    setCurrentParams(params);

    try {
      const data = await reportService.getDauReports(params);
      setState(prev => ({ ...prev, data, loading: false }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch DAU reports';
      setState(prev => ({ 
        ...prev, 
        error: errorMessage,
        loading: false 
      }));
      throw error;
    }
  }, [isAuthenticated, user?.roles]);

  const refetch = useCallback(() => {
    return fetchReports(currentParams);
  }, [fetchReports, currentParams]);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // Note: Initial fetch is handled by parent Reports page via handleApplyFilter()
  // No auto-fetch on mount to avoid race conditions and inconsistent data

  return {
    ...state,
    fetchReports,
    refetch,
    clearError,
  };
};